import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { exitApp, onNativeBack } from '@/lib/appBack';
import { runTopBackHandler } from '@/hooks/useBackHandler';

/**
 * Az Android vissza gomb viselkedése — EGY helyen, az app gyökerén.
 *
 * A sorrend fentről lefelé, és pontosan ezt látja a felhasználó:
 *
 *   1. Van nyitott réteg (lap, párbeszéd, nagyított kép)? → azt zárjuk be.
 *   2. Nem a Home-on állunk? → pontosan az úton vissza, amin jöttünk (végül
 *      a Home-ra).
 *   3. A Home-on vagyunk? → NEM lépünk ki azonnal, hanem kérünk még egy
 *      megnyomást.
 *
 * ⚠️ A HÁRMAS PONT A LÉNYEG, ezért van külön kezelve. Korábban a vissza gomb
 * a Home-on egyetlen koppintással bezárta az egész appot — rögzítés közben ez
 * különösen rossz. A kétlépcsős kilépés az Android megszokott mintája.
 */

/**
 * Az EGYETLEN gyökér a Kezdőlap. A többi fül (Grund, Közösség, Profil) NEM
 * az: onnan a vissza gomb visszalép (végül a Home-ra), és csak a Home-on
 * kérünk kétszeri megnyomást a kilépéshez.
 */
const ROOT_PATH = '/';

/** Ennyi ideig számít a második megnyomás megerősítésnek. */
const EXIT_CONFIRM_MS = 2500;

export interface AndroidBackState {
  /** Látszik-e a „még egyszer a kilépéshez" üzenet. */
  confirmingExit: boolean;
}

export function useAndroidBack(): AndroidBackState {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmingExit, setConfirmingExit] = useState(false);

  /*
    Az útvonal REFBEN is kell.

    A natív feliratkozás egyszer jön létre, és a `handler` abban a
    pillanatban látott útvonalat zárná magába örökre. Refből mindig a
    mostani érték olvasható, és nem kell minden navigációnál újra
    fel-leiratkozni a natív eseményre.
  */
  const path = useRef(location.pathname);
  path.current = location.pathname;
  // A `key` „default", ha ez a munkamenet ELSŐ bejegyzése — nincs app-beli
  // előzmény, amire `navigate(-1)` visszaléphetne (pl. pushból nyitott
  // képernyő). Ilyenkor a Home-ra megyünk, nem a semmibe.
  const hasHistory = useRef(location.key !== 'default');
  hasHistory.current = location.key !== 'default';

  /*
    ⚠️ A `navigate` REFBEN, és a natív feliratkozás EGYSZER jön létre.

    BrowserRouterben a `useNavigate()` azonossága MINDEN útvonalváltáskor
    megváltozik. Korábban ez volt a `useEffect` függősége, így minden
    navigáció leiratkozott és újra feliratkozott a natív eseményre — közben a
    natív callback egy pillanatra ki volt kapcsolva (a rendszer alapértelmezése
    futott: kilépés/előzménylépés), a cleanup pedig törölte a kilépés-időzítőt,
    de az `awaitingExit`/`confirmingExit` állapotot bent hagyta: a felirat
    beragadt (Samsung S23-on jelentve, GRUNDO #69 — ez FELTÉTELEZÉS a kódból,
    készüléken nem mérve).
  */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const exitTimer = useRef<number | undefined>(undefined);
  const awaitingExit = useRef(false);

  // Útvonalváltáskor a függő kilépés-megerősítés érvényét veszti.
  useEffect(() => {
    window.clearTimeout(exitTimer.current);
    awaitingExit.current = false;
    setConfirmingExit(false);
  }, [location.pathname]);

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;

    const handleBack = () => {
      // 1. Nyitott réteg bezárása.
      if (runTopBackHandler()) return;

      // 2. Nem a Home: pontosan azt az utat járjuk vissza, amin jöttünk.
      if (path.current !== ROOT_PATH) {
        if (hasHistory.current) navigateRef.current(-1);
        else navigateRef.current(ROOT_PATH, { replace: true });
        return;
      }

      // 3. Home: megerősítés, majd kilépés.
      if (awaitingExit.current) {
        window.clearTimeout(exitTimer.current);
        void exitApp();
        return;
      }

      awaitingExit.current = true;
      setConfirmingExit(true);
      window.clearTimeout(exitTimer.current);
      exitTimer.current = window.setTimeout(() => {
        awaitingExit.current = false;
        setConfirmingExit(false);
      }, EXIT_CONFIRM_MS);
    };

    void onNativeBack(handleBack).then((off) => {
      // Ha a komponens a feliratkozás befejezése ELŐTT eltűnt, azonnal
      // bontjuk — különben a natív oldal egy halott kezelőnek küldene.
      if (disposed) off();
      else unsubscribe = off;
    });

    return () => {
      disposed = true;
      window.clearTimeout(exitTimer.current);
      unsubscribe?.();
    };
  }, []);

  return { confirmingExit };
}
